import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { getStudents, uploadFile } from "../../lib/api";

const CATEGORIES = [
  { value: "iep", label: "IEP" },
  { value: "contract", label: "Contract" },
  { value: "progress_report", label: "Progress Report" },
  { value: "proficiency", label: "Proficiency Report" },
  { value: "clinical_report", label: "Clinical Report" },
  { value: "supporting_doc", label: "Supporting Document" },
];

// Take the first two characters of the filename as initials and try to find
// exactly one confident student match. Convention: char 1 = first-name initial,
// char 2 = last-name initial. Checks BOTH legal first name and preferred name.
function autoMatchStudentId(filename, students) {
  const initials = (filename || "").trim().slice(0, 2).toLowerCase();
  if (initials.length < 2) return null;
  const firstInitial = initials[0];
  const lastInitial = initials[1];
  const matches = students.filter((s) => {
    const ln = (s.last_name || "").trim().toLowerCase();
    const fn = (s.first_name || "").trim().toLowerCase();
    const pn = (s.preferred_name || "").trim().toLowerCase();
    if (!ln) return false;
    const lastOk = ln[0] === lastInitial;
    const firstOk = fn[0] === firstInitial || (pn && pn[0] === firstInitial);
    return lastOk && firstOk;
  });
  return matches.length === 1 ? matches[0].id : null;
}

function studentLabel(s) {
  const legal = `${s.first_name || ""} ${s.last_name || ""}`.trim();
  const pref = (s.preferred_name || "").trim();
  return pref && pref.toLowerCase() !== (s.first_name || "").trim().toLowerCase()
    ? `${legal} ("${pref}")`
    : legal;
}

export function BulkDocumentUpload({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);

  const sortedStudents = [...students].sort((a, b) =>
    `${a.last_name || ""}${a.first_name || ""}`.localeCompare(`${b.last_name || ""}${b.first_name || ""}`)
  );

  const handleOpenChange = async (v) => {
    setOpen(v);
    if (v) {
      setRows([]);
      setSummary(null);
      try {
        // Full roster (year-round, not summer-filtered) so any student is matchable.
        const res = await getStudents(null, true);
        setStudents(res.data || []);
      } catch {
        toast.error("Failed to load student list");
      }
    }
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSummary(null);
    const newRows = files.map((file, idx) => {
      const matchedId = autoMatchStudentId(file.name, students);
      return {
        rowId: `${Date.now()}-${idx}-${file.name}`,
        file,
        filename: file.name,
        studentId: matchedId || "",
        autoMatchedId: matchedId,
        category: "progress_report",
      };
    });
    setRows((prev) => [...prev, ...newRows]);
    e.target.value = "";
  };

  const updateRow = (rowId, patch) =>
    setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));

  const removeRow = (rowId) => setRows((prev) => prev.filter((r) => r.rowId !== rowId));

  const readyCount = rows.filter((r) => r.studentId).length;
  const autoFilledCount = rows.filter((r) => r.autoMatchedId).length;

  const handleConfirm = async () => {
    const toUpload = rows.filter((r) => r.studentId);
    if (!toUpload.length) {
      toast.error("Assign at least one file to a student before confirming");
      return;
    }
    setUploading(true);
    let autoMatched = 0;
    let manual = 0;
    let failed = 0;
    for (const r of toUpload) {
      try {
        await uploadFile(r.studentId, r.category, r.file);
        if (r.autoMatchedId && r.autoMatchedId === r.studentId) autoMatched += 1;
        else manual += 1;
      } catch {
        failed += 1;
      }
    }
    const skipped = rows.length - toUpload.length;
    setSummary({ total: rows.length, autoMatched, manual, failed, skipped });
    setUploading(false);
    if (failed === 0) {
      toast.success(`Uploaded ${autoMatched + manual} document(s)`);
    } else {
      toast.warning(`Uploaded ${autoMatched + manual}, ${failed} failed`);
    }
    onUploaded?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-upload-docs-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 18v-6"/><path d="m9 15 3-3 3 3"/></svg>
          Bulk Upload Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Upload Documents</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pt-2">
          {!summary && (
            <>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                Select one or more PDFs. We read the first two characters of each filename as
                initials (first + last name) and auto-fill the student when there is exactly one
                confident match against the legal or preferred name. Review and adjust the student
                and category for each file, then confirm.
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFilesSelected}
                  className="text-sm"
                  data-testid="bulk-upload-file-input"
                />
                {rows.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {rows.length} file(s) · {autoFilledCount} auto-matched · {readyCount} ready
                  </span>
                )}
              </div>

              {rows.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="border-b text-left">
                        <th className="py-2 px-3">File</th>
                        <th className="py-2 px-3 w-64">Student</th>
                        <th className="py-2 px-3 w-48">Category</th>
                        <th className="py-2 px-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.rowId} className="border-b align-middle" data-testid={`bulk-row-${r.filename}`}>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[220px]" title={r.filename}>{r.filename}</span>
                              {r.autoMatchedId && r.autoMatchedId === r.studentId && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">auto</span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <Select value={r.studentId} onValueChange={(v) => updateRow(r.rowId, { studentId: v })}>
                              <SelectTrigger data-testid={`bulk-student-select-${r.filename}`} className={!r.studentId ? "border-amber-400" : ""}>
                                <SelectValue placeholder="Select student…" />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {sortedStudents.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>{studentLabel(s)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-3">
                            <Select value={r.category} onValueChange={(v) => updateRow(r.rowId, { category: v })}>
                              <SelectTrigger data-testid={`bulk-category-select-${r.filename}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CATEGORIES.map((c) => (
                                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-3">
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => removeRow(r.rowId)} data-testid={`bulk-remove-${r.filename}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {summary && (
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 space-y-2" data-testid="bulk-upload-summary">
              <p className="font-medium text-green-800">Upload complete</p>
              <ul className="text-sm text-green-800 space-y-1">
                <li><strong>{summary.autoMatched}</strong> auto-matched by initials</li>
                <li><strong>{summary.manual}</strong> assigned manually</li>
                {summary.skipped > 0 && <li className="text-amber-700"><strong>{summary.skipped}</strong> skipped (no student selected)</li>}
                {summary.failed > 0 && <li className="text-red-700"><strong>{summary.failed}</strong> failed to upload</li>}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          {summary ? (
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => setOpen(false)} data-testid="bulk-upload-done-btn">Done</Button>
          ) : (
            <Button
              className="bg-[#00305E] hover:bg-[#00305E]/90"
              disabled={uploading || readyCount === 0}
              onClick={handleConfirm}
              data-testid="bulk-upload-confirm-btn"
            >
              {uploading ? "Uploading…" : `Confirm & Upload (${readyCount})`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
